import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "../../../../lib/queryKeys";
import {
  questionsApi,
  type AdminQuestion,
  type CreateQuestionRequest,
  type DynamicSource,
  type QuestionOption,
  type QuestionType,
  type UpdateQuestionRequest,
} from "../api/questions";
import { clientManagementApi, type ClientSummary } from "../api/clientManagement";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  single: "Single choice",
  multi: "Multi choice",
  text: "Text",
  slider: "Slider",
  dynamic_select: "Dynamic select",
  qrcode: "QR Code",
};

const QUESTION_TYPES: QuestionType[] = [
  "single",
  "multi",
  "text",
  "slider",
  "dynamic_select",
];

const DYNAMIC_SOURCES: { value: DynamicSource; label: string }[] = [
  { value: "department", label: "Department" },
  { value: "job_role", label: "Job Role" },
];

// ---------------------------------------------------------------------------
// Option editor row
// ---------------------------------------------------------------------------

interface OptionRowProps {
  option: {
    id?: string;
    value: string;
    label: string;
    description?: string | null;
    display_order?: number;
  };
  onRemove: () => void;
  onChange: (field: "value" | "label" | "description", value: string) => void;
  isGlobal: boolean;
}

const OptionEditorRow: React.FC<OptionRowProps> = ({
  option,
  onRemove,
  onChange,
  isGlobal,
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <TextField
      size="small"
      label="Value"
      value={option.value}
      onChange={(e) => onChange("value", e.target.value)}
      disabled={isGlobal}
      sx={{ minWidth: 100 }}
    />
    <TextField
      size="small"
      label="Label"
      value={option.label}
      onChange={(e) => onChange("label", e.target.value)}
      disabled={isGlobal}
      sx={{ flexGrow: 1 }}
    />
    <TextField
      size="small"
      label="Description"
      value={option.description ?? ""}
      onChange={(e) => onChange("description", e.target.value)}
      disabled={isGlobal}
      sx={{ flexGrow: 1 }}
    />
    {!isGlobal && (
      <Button size="small" color="error" onClick={onRemove}>
        Remove
      </Button>
    )}
  </Stack>
);

// ---------------------------------------------------------------------------
// Question edit/create dialog
// ---------------------------------------------------------------------------

interface EditDialogOption {
  id?: string;
  value: string;
  label: string;
  description?: string | null;
}

interface QuestionDialogProps {
  open: boolean;
  question: AdminQuestion | null;
  clientId: string | null;
  isOnboardingSection: boolean;
  onClose: () => void;
  onSave: (data: CreateQuestionRequest | (UpdateQuestionRequest & { id: string })) => void;
  isPending: boolean;
  onAddOption: (questionId: string, value: string, label: string) => void;
  onRemoveOption: (questionId: string, optionId: string) => void;
  isAddOptionPending: boolean;
  isRemoveOptionPending: boolean;
}

const emptyOption = (): EditDialogOption => ({ value: "", label: "", description: null });

const QuestionDialog: React.FC<QuestionDialogProps> = ({
  open,
  question,
  clientId,
  isOnboardingSection,
  onClose,
  onSave,
  isPending,
  onAddOption,
  onRemoveOption,
  isAddOptionPending,
  isRemoveOptionPending,
}) => {
  const isCreating = question === null;
  const isGlobal = !isCreating && question.client_id === null;

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [questionType, setQuestionType] = useState<QuestionType>("text");
  const [isActive, setIsActive] = useState(true);
  const [isOptional, setIsOptional] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [dynamicSource, setDynamicSource] = useState<DynamicSource | "">("");
  const [options, setOptions] = useState<EditDialogOption[]>([]);
  const [newOption, setNewOption] = useState(emptyOption());

  useEffect(() => {
    if (!open) return;
    if (question) {
      setTitle(question.title);
      setSubtitle(question.subtitle ?? "");
      setQuestionType(question.question_type);
      setIsActive(question.is_active);
      setIsOptional(question.is_optional);
      setDisplayOrder(question.display_order);
      setDynamicSource((question.dynamic_source as DynamicSource) ?? "");
      setOptions(
        question.options.map((o) => ({
          id: o.id,
          value: o.value,
          label: o.label,
          description: o.description,
        }))
      );
    } else {
      setTitle("");
      setSubtitle("");
      setQuestionType("text");
      setIsActive(true);
      setIsOptional(false);
      setDisplayOrder(0);
      setDynamicSource("");
      setOptions([]);
    }
    setNewOption(emptyOption());
  }, [open, question]);

  const handleOptionChange = (
    index: number,
    field: "value" | "label" | "description",
    value: string
  ) => {
    setOptions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleRemoveLocalOption = (index: number) => {
    const opt = options[index];
    if (opt.id && question) {
      // Persisted option on an existing question — delete via API
      onRemoveOption(question.id, opt.id);
    }
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddNewOption = () => {
    if (!newOption.value.trim() || !newOption.label.trim()) return;
    if (question && !isCreating) {
      // Existing question — add via API immediately
      onAddOption(question.id, newOption.value.trim(), newOption.label.trim());
    } else {
      // New question — accumulate locally
      setOptions((prev) => [...prev, { ...newOption }]);
    }
    setNewOption(emptyOption());
  };

  const handleSave = () => {
    if (!title.trim()) return;
    if (isCreating) {
      const payload: CreateQuestionRequest = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        question_type: questionType,
        is_onboarding: isOnboardingSection,
        is_pre_registration: !isOnboardingSection,
        display_order: displayOrder,
        is_active: isActive,
        is_optional: isOptional,
        dynamic_source: dynamicSource || null,
        client_id: clientId,
        options: options.map((o, i) => ({
          value: o.value,
          label: o.label,
          description: o.description ?? null,
          display_order: i,
        })),
      };
      onSave(payload);
    } else {
      if (isGlobal) {
        // Global: only is_active and is_optional may change
        onSave({ id: question.id, is_active: isActive, is_optional: isOptional });
      } else {
        const payload: UpdateQuestionRequest & { id: string } = {
          id: question.id,
          title: title.trim(),
          subtitle: subtitle.trim() || null,

          is_onboarding: isOnboardingSection,
          is_pre_registration: !isOnboardingSection,
          display_order: displayOrder,
          is_active: isActive,
          is_optional: isOptional,
          dynamic_source: dynamicSource || null,
        };
        onSave(payload);
      }
    }
  };

  const showOptions =
    questionType === "single" || questionType === "multi";
  const showDynamicSource = questionType === "dynamic_select";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isCreating
          ? clientId === null
            ? "Add Default Question"
            : "Add Question"
          : isGlobal
          ? "View / Toggle Global Question"
          : "Edit Question"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isGlobal && (
            <Alert severity="info">
              This is a global (default) question. Only the active/inactive
              status can be changed.
            </Alert>
          )}
          {isCreating && clientId === null && (
            <Alert severity="info">
              This will create a global default question visible to all clients
              that have not customised their question set.
            </Alert>
          )}

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="small"
            fullWidth
            disabled={isGlobal}
            required
          />

          <TextField
            label="Subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            size="small"
            fullWidth
            disabled={isGlobal}
          />

          {!isGlobal && (
            <TextField
              select
              label="Question Type"
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value as QuestionType)}
              size="small"
              disabled={!isCreating}
            >
              {QUESTION_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {QUESTION_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </TextField>
          )}

          {showDynamicSource && !isGlobal && (
            <TextField
              select
              label="Dynamic Source"
              value={dynamicSource}
              onChange={(e) => setDynamicSource(e.target.value as DynamicSource | "")}
              size="small"
            >
              <MenuItem value="">None</MenuItem>
              {DYNAMIC_SOURCES.map((ds) => (
                <MenuItem key={ds.value} value={ds.value}>
                  {ds.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(_e, checked) => setIsActive(checked)}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!isOptional}
                  onChange={(_e, checked) => setIsOptional(!checked)}
                />
              }
              label="Required"
            />
          </Stack>

          {!isGlobal && (
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
              size="small"
              sx={{ maxWidth: 140 }}
            />
          )}

          {showOptions && (
            <Box>
              <Typography variant="subtitle2" mb={1}>
                Options
              </Typography>
              <Stack spacing={1}>
                {options.map((opt, i) => (
                  <OptionEditorRow
                    key={opt.id ?? i}
                    option={opt}
                    isGlobal={isGlobal}
                    onChange={(field, value) =>
                      handleOptionChange(i, field, value)
                    }
                    onRemove={() => handleRemoveLocalOption(i)}
                  />
                ))}
              </Stack>
              {!isGlobal && (
                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                  <TextField
                    size="small"
                    label="New Value"
                    value={newOption.value}
                    onChange={(e) =>
                      setNewOption((prev) => ({
                        ...prev,
                        value: e.target.value,
                      }))
                    }
                    sx={{ minWidth: 100 }}
                  />
                  <TextField
                    size="small"
                    label="New Label"
                    value={newOption.label}
                    onChange={(e) =>
                      setNewOption((prev) => ({
                        ...prev,
                        label: e.target.value,
                      }))
                    }
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAddNewOption}
                    disabled={
                      !newOption.value.trim() ||
                      !newOption.label.trim() ||
                      isAddOptionPending
                    }
                  >
                    Add
                  </Button>
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending || (!isGlobal && !title.trim())}
        >
          {isPending ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Questions table for one section
// ---------------------------------------------------------------------------

interface QuestionsSectionTableProps {
  questions: AdminQuestion[];
  onEdit: (question: AdminQuestion) => void;
  onDelete: (question: AdminQuestion) => void;
  isDeletePending: boolean;
  deletingId: string | null;
}

const QuestionsSectionTable: React.FC<QuestionsSectionTableProps> = ({
  questions,
  onEdit,
  onDelete,
  isDeletePending,
  deletingId,
}) => {
  if (!questions.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No questions found.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Order</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {questions.map((q) => {
            const isGlobal = q.client_id === null;
            const isDeletable = !isGlobal && q.question_type !== "qrcode";
            return (
              <TableRow key={q.id} hover>
                <TableCell>
                  <Typography variant="body2">{q.title}</Typography>
                  {q.subtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {q.subtitle}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {QUESTION_TYPE_LABELS[q.question_type] ?? q.question_type}
                  </Typography>
                </TableCell>
                <TableCell>{q.display_order}</TableCell>
                <TableCell>
                  {isGlobal ? (
                    <Chip label="Global (default)" size="small" color="default" />
                  ) : (
                    <Chip label="Client-specific" size="small" color="primary" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {q.is_active ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Chip label="Inactive" size="small" color="default" />
                    )}
                    <Chip
                      label={q.is_optional ? "Optional" : "Required"}
                      size="small"
                      variant="outlined"
                      color={q.is_optional ? "default" : "warning"}
                    />
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title={isGlobal ? "Toggle active / view" : "Edit"}>
                      <Button size="small" onClick={() => onEdit(q)}>
                        {isGlobal ? "Toggle" : "Edit"}
                      </Button>
                    </Tooltip>
                    {isDeletable && (
                      <Tooltip title="Delete">
                        <Button
                          size="small"
                          color="error"
                          onClick={() => onDelete(q)}
                          disabled={isDeletePending && deletingId === q.id}
                        >
                          {isDeletePending && deletingId === q.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Delete"
                          )}
                        </Button>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---------------------------------------------------------------------------
// Main QuestionsTab component
// ---------------------------------------------------------------------------

interface QuestionsTabProps {
  effectiveClientId: string | null;
  isCentralAdmin: boolean;
}

interface CopyFromClientDialogProps {
  open: boolean;
  targetClientId: string;
  onClose: () => void;
  onConfirm: (sourceClientId: string, section: "onboarding" | "pre_registration" | "both") => void;
  isPending: boolean;
}

const CopyFromClientDialog: React.FC<CopyFromClientDialogProps> = ({
  open,
  targetClientId,
  onClose,
  onConfirm,
  isPending,
}) => {
  const [sourceClient, setSourceClient] = useState<ClientSummary | null>(null);
  const [section, setSection] = useState<"onboarding" | "pre_registration" | "both">("both");

  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const availableClients = (clientsQuery.data?.data ?? []).filter(
    (c) => c.id !== targetClientId
  );

  const handleConfirm = () => {
    if (!sourceClient) return;
    onConfirm(sourceClient.id, section);
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setSourceClient(null);
      setSection("both");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Copy questions from another client</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning" variant="outlined">
            This will copy the selected client's questions to this client. Only
            sections with no existing questions will be populated.
          </Alert>
          <Autocomplete
            options={availableClients}
            loading={clientsQuery.isLoading}
            value={sourceClient}
            onChange={(_e, val) => setSourceClient(val)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label="Source client" size="small" />
            )}
          />
          <TextField
            select
            label="Section"
            size="small"
            value={section}
            onChange={(e) =>
              setSection(e.target.value as "onboarding" | "pre_registration" | "both")
            }
          >
            <MenuItem value="both">Both (Onboarding & Pre-registration)</MenuItem>
            <MenuItem value="onboarding">Onboarding only</MenuItem>
            <MenuItem value="pre_registration">Pre-registration only</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!sourceClient || isPending}
        >
          {isPending ? <CircularProgress size={18} /> : "Copy questions"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuestionsTab: React.FC<QuestionsTabProps> = ({
  effectiveClientId,
  isCentralAdmin,
}) => {
  const queryClient = useQueryClient();
  const [sectionTab, setSectionTab] = useState<"onboarding" | "pre_registration">(
    "onboarding"
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<AdminQuestion | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const questionsQuery = useQuery({
    queryKey: qk.adminQuestions(effectiveClientId, "all"),
    queryFn: () => questionsApi.listQuestions(effectiveClientId, "all"),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const allQuestions: AdminQuestion[] = questionsQuery.data?.data ?? [];

  const onboardingQuestions = allQuestions
    .filter((q) => q.is_onboarding)
    .sort((a, b) => {
      if ((a.client_id === null) !== (b.client_id === null)) {
        return a.client_id === null ? 1 : -1;
      }
      return a.display_order - b.display_order;
    });

  const preRegQuestions = allQuestions
    .filter((q) => q.is_pre_registration)
    .sort((a, b) => {
      if ((a.client_id === null) !== (b.client_id === null)) {
        return a.client_id === null ? 1 : -1;
      }
      return a.display_order - b.display_order;
    });

  // Whether the current section has ANY client-specific questions
  const clientOnboardingCount = onboardingQuestions.filter(
    (q) => q.client_id !== null
  ).length;
  const clientPreRegCount = preRegQuestions.filter(
    (q) => q.client_id !== null
  ).length;
  const currentSectionHasClientSet =
    sectionTab === "onboarding" ? clientOnboardingCount > 0 : clientPreRegCount > 0;

  const invalidateQuestions = () => {
    queryClient.invalidateQueries({
      queryKey: qk.adminQuestions(effectiveClientId, "all"),
    });
  };

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof questionsApi.createQuestion>[0]) =>
      questionsApi.createQuestion(payload),
    onSuccess: () => {
      invalidateQuestions();
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...payload
    }: UpdateQuestionRequest & { id: string }) =>
      questionsApi.updateQuestion(id, payload),
    onSuccess: () => {
      invalidateQuestions();
      setDialogOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (questionId: string) =>
      questionsApi.deleteQuestion(questionId),
    onMutate: (id) => setDeletingId(id),
    onSuccess: () => {
      invalidateQuestions();
    },
    onSettled: () => setDeletingId(null),
  });

  const initialiseMutation = useMutation({
    mutationFn: (section: "onboarding" | "pre_registration") =>
      questionsApi.initialiseClientSet(effectiveClientId!, section),
    onSuccess: () => {
      invalidateQuestions();
    },
  });

  const copyFromClientMutation = useMutation({
    mutationFn: ({
      sourceClientId,
      section,
    }: {
      sourceClientId: string;
      section: "onboarding" | "pre_registration" | "both";
    }) =>
      questionsApi.copyFromClient(effectiveClientId!, sourceClientId, section),
    onSuccess: () => {
      invalidateQuestions();
      setCopyDialogOpen(false);
    },
  });

  const addOptionMutation = useMutation({
    mutationFn: ({
      questionId,
      value,
      label,
    }: {
      questionId: string;
      value: string;
      label: string;
    }) => questionsApi.addOption(questionId, { value, label }),
    onSuccess: () => invalidateQuestions(),
  });

  const removeOptionMutation = useMutation({
    mutationFn: ({
      questionId,
      optionId,
    }: {
      questionId: string;
      optionId: string;
    }) => questionsApi.removeOption(questionId, optionId),
    onSuccess: () => invalidateQuestions(),
  });

  const [creatingDefault, setCreatingDefault] = useState(false);

  const handleOpenCreate = () => {
    setCreatingDefault(false);
    setEditingQuestion(null);
    setDialogOpen(true);
  };

  const handleOpenCreateDefault = () => {
    setCreatingDefault(true);
    setEditingQuestion(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (question: AdminQuestion) => {
    setCreatingDefault(false);
    setEditingQuestion(question);
    setDialogOpen(true);
  };

  const handleDelete = (question: AdminQuestion) => {
    if (!window.confirm(`Delete question "${question.title}"?`)) return;
    deleteMutation.mutate(question.id);
  };

  const handleInitialise = () => {
    if (
      !window.confirm(
        `This will create a full copy of all default ${sectionTab === "onboarding" ? "onboarding" : "pre-registration"} questions for this client. ` +
          "After this, changes only affect the client's set and do not impact the global defaults. Continue?"
      )
    )
      return;
    initialiseMutation.mutate(sectionTab);
  };

  const handleSave = (
    data: Parameters<typeof questionsApi.createQuestion>[0] | (UpdateQuestionRequest & { id: string })
  ) => {
    if ("id" in data && editingQuestion) {
      const { id, ...rest } = data as UpdateQuestionRequest & { id: string };
      updateMutation.mutate({ id, ...rest });
    } else {
      createMutation.mutate(
        data as Parameters<typeof questionsApi.createQuestion>[0]
      );
    }
  };

  if (!isCentralAdmin) {
    return (
      <SettingsSection
        title="Questions"
        description="Central admin access required."
      >
        <Alert severity="warning">
          You do not have permission to manage questions.
        </Alert>
      </SettingsSection>
    );
  }

  // Once a client has their own set, only show their questions (not global defaults)
  const allSectionQuestions =
    sectionTab === "onboarding" ? onboardingQuestions : preRegQuestions;
  const activeQuestions =
    effectiveClientId && currentSectionHasClientSet
      ? allSectionQuestions.filter((q) => q.client_id !== null)
      : allSectionQuestions;

  const isMutationPending =
    createMutation.isPending || updateMutation.isPending;

  return (
    <SettingsSection
      title="Questions"
      description="Manage onboarding and pre-registration questions. Global questions apply to all clients unless a client has their own set."
    >
      {!effectiveClientId && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Select a client to manage client-specific questions. Global questions
          are always shown.
        </Alert>
      )}

      {(createMutation.isError || updateMutation.isError || deleteMutation.isError) && (
        <Alert severity="error">An error occurred. Please try again.</Alert>
      )}

      {initialiseMutation.isError && (
        <Alert severity="error">
          Failed to initialise question set. The client may already have questions
          in this section.
        </Alert>
      )}

      {copyFromClientMutation.isError && (
        <Alert severity="error">
          Failed to copy questions. The target client may already have questions
          in the selected section.
        </Alert>
      )}

      <SectionCard title="Question Bank">
        <Stack spacing={2}>
          <Tabs
            value={sectionTab}
            onChange={(_e, v) => setSectionTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Tab
              value="onboarding"
              label={`Onboarding (${clientOnboardingCount > 0 && effectiveClientId ? clientOnboardingCount : onboardingQuestions.length})`}
              sx={{ textTransform: "none" }}
            />
            <Tab
              value="pre_registration"
              label={`Pre-Registration (${clientPreRegCount > 0 && effectiveClientId ? clientPreRegCount : preRegQuestions.length})`}
              sx={{ textTransform: "none" }}
            />
          </Tabs>

          {/* Client customisation status banner */}
          {effectiveClientId && !questionsQuery.isLoading && (
            currentSectionHasClientSet ? (
              <Alert severity="success" icon={false}>
                This client has their own{" "}
                {sectionTab === "onboarding" ? "onboarding" : "pre-registration"}{" "}
                question set. Add, edit, or delete questions below — changes only
                affect this client.
              </Alert>
            ) : (
              <Alert severity="info">
                <Stack spacing={1.5}>
                  <Typography variant="body2">
                    This client is using the global default questions. Initialise a
                    client-specific set to customise independently.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={handleInitialise}
                      disabled={initialiseMutation.isPending || copyFromClientMutation.isPending}
                    >
                      {initialiseMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : (
                        "Initialise from Defaults"
                      )}
                    </Button>
                    {isCentralAdmin && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={() => setCopyDialogOpen(true)}
                        disabled={initialiseMutation.isPending || copyFromClientMutation.isPending}
                      >
                        Copy from another client
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Alert>
            )
          )}

          {questionsQuery.isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading questions...</Typography>
            </Stack>
          ) : questionsQuery.isError ? (
            <Alert severity="error">Failed to load questions.</Alert>
          ) : (
            <QuestionsSectionTable
              questions={activeQuestions}
              onEdit={handleOpenEdit}
              onDelete={handleDelete}
              isDeletePending={deleteMutation.isPending}
              deletingId={deletingId}
            />
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {effectiveClientId && currentSectionHasClientSet && (
              <Button variant="outlined" size="small" onClick={handleOpenCreate}>
                Add Question
              </Button>
            )}
            {isCentralAdmin && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenCreateDefault}
              >
                Add Default Question
              </Button>
            )}
            {effectiveClientId && currentSectionHasClientSet && isCentralAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() => setCopyDialogOpen(true)}
              >
                Copy from another client
              </Button>
            )}
          </Stack>
        </Stack>
      </SectionCard>

      <QuestionDialog
        open={dialogOpen}
        question={editingQuestion}
        clientId={creatingDefault ? null : effectiveClientId}
        isOnboardingSection={sectionTab === "onboarding"}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        isPending={isMutationPending}
        onAddOption={(questionId, value, label) =>
          addOptionMutation.mutate({ questionId, value, label })
        }
        onRemoveOption={(questionId, optionId) =>
          removeOptionMutation.mutate({ questionId, optionId })
        }
        isAddOptionPending={addOptionMutation.isPending}
        isRemoveOptionPending={removeOptionMutation.isPending}
      />

      {effectiveClientId && isCentralAdmin && (
        <CopyFromClientDialog
          open={copyDialogOpen}
          targetClientId={effectiveClientId}
          onClose={() => setCopyDialogOpen(false)}
          onConfirm={(sourceClientId, section) =>
            copyFromClientMutation.mutate({ sourceClientId, section })
          }
          isPending={copyFromClientMutation.isPending}
        />
      )}
    </SettingsSection>
  );
};
